/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import * as z from "zod";
import type { ToZod } from "../.kubb/ToZod.ts";
import type { CourseEditorViewModel } from "../types/CourseEditorViewModel.ts";
import { eCourseAreaSchema } from "./eCourseAreaSchema.ts";
import { eCourseHistoryTypeSchema } from "./eCourseHistoryTypeSchema.ts";
import { eCourseModalitySchema } from "./eCourseModalitySchema.ts";
import { eCourseOnDemandProgressionTypeSchema } from "./eCourseOnDemandProgressionTypeSchema.ts";
import { eCourseStatusSchema } from "./eCourseStatusSchema.ts";
import { eRegistrationFormTypeSchema } from "./eRegistrationFormTypeSchema.ts";
import { eTypeCourseSchema } from "./eTypeCourseSchema.ts";

export const courseEditorViewModelSchema = z.object({
  code: z
    .string()
    .describe(
      "Código do curso. Obrigatório, máximo de 15 caracteres e único por empresa.",
    )
    .nullish(),
  codeOld: z
    .string()
    .describe(
      "Código anterior do curso. Máximo de 15 caracteres, aceita apenas letras, números e hífen. Opcional.",
    )
    .nullish(),
  codePayment: z
    .string()
    .describe(
      "Código de integração da mensalidade. Máximo de 30 caracteres, aceita apenas letras, números e hífen. Opcional.",
    )
    .nullish(),
  codeRegistration: z
    .string()
    .describe(
      "Código de integração da inscrição. Máximo de 30 caracteres, aceita apenas letras, números e hífen. Opcional.",
    )
    .nullish(),
  status: z.optional(
    z
      .lazy(() => eCourseStatusSchema)
      .describe("1 = Released | 2 = Canceled | 3 = Finalized | "),
  ),
  modality: z.optional(
    z
      .lazy(() => eCourseModalitySchema)
      .describe(
        "1 = Distance | 2 = Presential | 3 = SemiClassroom | 4 = OnDemand | ",
      ),
  ),
  typeCourse: z.optional(
    z
      .lazy(() => eTypeCourseSchema)
      .describe(
        "1 = PostGraduation | 2 = Improvement | 3 = Update | 4 = Diffusion | 5 = FreeCourse | 6 = Event | 7 = MbaUpdate | 8 = Graduation | 9 = Workshop | 10 = Lecture | ",
      ),
  ),
  courseArea: z.optional(
    z
      .lazy(() => eCourseAreaSchema)
      .describe(
        "1 = Exact | 2 = Biological | 3 = Human | 4 = VeterinaryMedicine | ",
      ),
  ),
  registrationFormType: z.optional(
    z
      .lazy(() => eRegistrationFormTypeSchema)
      .describe(
        "1 = Complete | 2 = Compact | 3 = CompactWithVerification | 4 = Simple | 5 = Latest | 6 = LatestFMVZ | 7 = LatestPecegeCollege | 8 = LatestPoli | 9 = LatestCiaam | 10 = LatestPecegeCollegeMBA | 11 = LatestMiniCourse | ",
      ),
  ),
  historyType: z.optional(
    z
      .lazy(() => eCourseHistoryTypeSchema)
      .describe("1 = ByDiscipline | 2 = ByClassGroup | "),
  ),
  onDemandProgressionType: z.optional(
    z
      .lazy(() => eCourseOnDemandProgressionTypeSchema)
      .describe(
        "1 = None | 2 = Ordered | 3 = ClassDuration | 4 = Limited | 5 = Grade | ",
      ),
  ),
  onDemandProgressionLimitDays: z
    .number()
    .int()
    .describe(
      "Limite de dias da progressão OnDemand, entre 0 e 99. Obrigatório quando a progressão é Limited. Opcional nos demais casos.",
    )
    .nullish(),
  onDemandProgressionLimitClasses: z
    .number()
    .int()
    .describe(
      "Limite de aulas da progressão OnDemand, entre 0 e 99. Obrigatório quando a progressão é Limited. Opcional nos demais casos.",
    )
    .nullish(),
  onDemandProgressionExam: z.optional(
    z
      .boolean()
      .describe(
        "Define se a avaliação é exigida para progressão. Ignorado e gravado como verdadeiro quando a progressão é por nota, e como falso quando a progressão é None.",
      ),
  ),
  duration: z
    .number()
    .int()
    .describe("Duração do curso em dias, entre 0 e 9999. Opcional.")
    .nullish(),
  minGrade: z
    .number()
    .describe(
      "Nota mínima para aprovação, entre 0 e 10. Obrigatória quando a progressão OnDemand é por nota. Opcional nos demais casos.",
    )
    .nullish(),
  maxGrade: z
    .number()
    .describe(
      "Nota máxima do curso, entre 0 e 10. Não pode ser menor que a nota mínima. Opcional.",
    )
    .nullish(),
  gradePointAverage: z
    .number()
    .describe(
      "Média exigida para aprovação, entre 0 e 10. Obrigatória quando o certificado de conclusão é automático. Opcional nos demais casos.",
    )
    .nullish(),
  companyId: z
    .number()
    .int()
    .describe(
      "ID da empresa dona do curso. Ignorado quando o usuário autenticado é vinculado a uma empresa e não pode ser alterado na atualização. Opcional.",
    )
    .nullish(),
  courseGroupId: z
    .number()
    .int()
    .describe(
      "ID do grupo de curso. Precisa existir quando informado. Opcional.",
    )
    .nullish(),
  suggestedMonitorId: z
    .number()
    .int()
    .describe(
      "ID da pessoa sugerida como monitor. Em curso de empresa precisa ter perfil de monitor na empresa; em curso da matriz precisa estar marcada como monitor. Opcional.",
    )
    .nullish(),
  ssoCategoryId: z
    .number()
    .int()
    .describe("ID da categoria de SSO usada na integração. Opcional.")
    .nullish(),
  googleTagManagerIdentifier: z
    .string()
    .describe(
      "Identificador do Google Tag Manager, no padrão GTM-XXXXXX. Máximo de 100 caracteres. Opcional.",
    )
    .nullish(),
  active: z.optional(
    z
      .boolean()
      .default(true)
      .describe(
        "Define se o curso fica ativo. Ao inativar, a data de inativação é registrada automaticamente.",
      ),
  ),
  showInLogin: z.optional(
    z.boolean().describe("Define se o curso é destacado na tela de login."),
  ),
  openCourse: z.optional(z.boolean().describe("Define se o curso é aberto.")),
  requiredApproval: z.optional(
    z
      .boolean()
      .describe(
        "Define se o curso exige aprovação de documentação. A habilitação é recusada quando existem planos financeiros sem valor de inscrição.",
      ),
  ),
  automaticApproval: z.optional(
    z
      .boolean()
      .describe(
        "Define se o curso possui aprovação automática de matrícula. Precisa ser falso em curso de empresa.",
      ),
  ),
  automaticApprovalForCompany: z.optional(
    z
      .boolean()
      .describe(
        "Define se o curso possui aprovação automática para a empresa. Precisa ser falso em curso da matriz.",
      ),
  ),
  representativeRequired: z.optional(
    z
      .boolean()
      .describe(
        "Define se o curso exige representante da empresa. Precisa ser falso em curso da matriz.",
      ),
  ),
  monograph: z.optional(
    z
      .boolean()
      .describe(
        "Define se o curso possui monografia. Precisa ser falso em curso de empresa e é recusado quando alguma turma utiliza o Move TCC.",
      ),
  ),
  internationalCourse: z.optional(
    z.boolean().describe("Define se o curso é internacional."),
  ),
  accessHelpcenter: z.optional(
    z.boolean().describe("Define se o curso libera acesso à central de ajuda."),
  ),
  requirePostGraduateDocument: z.optional(
    z
      .boolean()
      .describe(
        "Define se o curso exige diploma de pós-graduação na matrícula.",
      ),
  ),
  requireUniversityDegree: z.optional(
    z
      .boolean()
      .describe("Define se o curso exige diploma de graduação na matrícula."),
  ),
  requireCodeUsp: z.optional(
    z.boolean().describe("Define se o curso exige o código USP do aluno."),
  ),
  mobileAccess: z.optional(
    z.boolean().describe("Define se o curso permite acesso pelo aplicativo."),
  ),
  onlyAccessActive: z.optional(
    z
      .boolean()
      .describe("Define se apenas matrículas ativas acessam o conteúdo."),
  ),
  validateContract: z.optional(
    z
      .boolean()
      .describe("Define se o contrato do curso exige validação do aluno."),
  ),
  automaticCompletionCertificate: z.optional(
    z
      .boolean()
      .describe(
        "Define se o certificado de conclusão é emitido automaticamente. Quando verdadeiro, a média para aprovação passa a ser obrigatória.",
      ),
  ),
  hasSelectiveProcess: z.optional(
    z.boolean().describe("Define se o curso possui processo seletivo."),
  ),
  showCommunicationLanguageCompactReg: z.optional(
    z
      .boolean()
      .describe(
        "Define se o idioma de comunicação é exibido na inscrição compacta.",
      ),
  ),
  releasePaymentSelection: z.optional(
    z
      .boolean()
      .describe(
        "Define se a seleção de pagamento libera o acesso à sala de aula.",
      ),
  ),
  releaseEnrollmentBatchStatusChange: z.optional(
    z
      .boolean()
      .describe(
        "Define se o curso permite alteração de status de matrícula em lote.",
      ),
  ),
  spReleasedUntil: z
    .string()
    .datetime()
    .describe(
      "Data limite de liberação de acesso para matrículas com seleção de pagamento. Opcional.",
    )
    .nullish(),
  ppAndPAReleasedUntil: z
    .string()
    .datetime()
    .describe(
      "Data limite de liberação de acesso para matrículas com pagamento pendente e aprovação pendente. Opcional.",
    )
    .nullish(),
  endActionWithAP: z
    .string()
    .datetime()
    .describe(
      "Data de encerramento da ação com aprovação pendente, usada na integração. Opcional.",
    )
    .nullish(),
  cultureInfo: z
    .string()
    .describe(
      "Idioma do conteúdo enviado. Obrigatório, precisa ser um idioma permitido e não pode ser alterado na atualização.",
    )
    .nullish(),
  nameOfficial: z
    .string()
    .describe("Nome oficial do curso. Obrigatório, máximo de 250 caracteres.")
    .nullish(),
  nameCommercial: z
    .string()
    .describe("Nome comercial do curso. Obrigatório, máximo de 250 caracteres.")
    .nullish(),
  description: z
    .string()
    .describe("Descrição do curso. Máximo de 4000 caracteres. Opcional.")
    .nullish(),
  contentHtml: z
    .string()
    .describe("Regulamento do curso em HTML. Opcional.")
    .nullish(),
  contentInstruction: z
    .string()
    .describe("Instruções do curso. Máximo de 4000 caracteres. Opcional.")
    .nullish(),
}) as unknown as ToZod<CourseEditorViewModel>;
